import { ActivityType } from '../models/design/activities/activity-type.model';
import type { DesignExpressionVariables } from '../models/design/design-expression-variables.model';
import type { Project } from '../models/project.model';
import type { Results } from '../models/unit.model';
import { getOccupantCountPerM2 } from './activities-residents.helpers';
import { getHeatedArea } from './energy/energy.helpers';
import { IVL_B_PHASE_REFERENCE_PERIOD_YEARS } from './lifecycles.data';
import { getBuildingDesignFromProjectOrBuilding } from './design/design.helpers';
import { getDesignExpressionVariables } from './design/expressions/design-expression-variables.helpers';

/**
 * Program activity types that consume occupant water, each with an IVL schablon in
 * {@link WATER_M3_PER_OCCUPANT_YEAR} (DEV-3326).
 */
const WATER_PROGRAM_ACTIVITY_TYPES = [
  ActivityType.Residential,
  ActivityType.Office,
  ActivityType.School,
  ActivityType.Retail,
  ActivityType.Industry,
] as const;

type WaterProgramActivityType = (typeof WATER_PROGRAM_ACTIVITY_TYPES)[number];

/**
 * Programs that take up primary building area without carrying occupants: garage is
 * unheated (see `NON_HEATED_ACTIVITY_TYPES`) and `Other` has no defined use. They get a
 * share of the heated area like any other program, but no water use is charged for it.
 */
const UNOCCUPIED_ACTIVITY_TYPES = [
  ActivityType.Garage,
  ActivityType.Other,
] as const;

/**
 * Operational water use per occupant and year (m³) per building use type.
 * IVL schablon values (DEV-3326).
 *
 * IVL also defines schablons for förskola (7.05), restaurang (182.5), hotell (109.5),
 * sjukhus (255.5) and vårdcentral (10.4). Those are omitted until the matching
 * {@link ActivityType} exists in the design tool.
 */
export const WATER_M3_PER_OCCUPANT_YEAR: Record<
  WaterProgramActivityType,
  number
> = {
  [ActivityType.Residential]: 49.28,
  [ActivityType.Office]: 9.4,
  [ActivityType.School]: 4.45,
  [ActivityType.Retail]: 14.6,
  [ActivityType.Industry]: 29.2,
};

/** Combined water (0.08) + wastewater (0.30) climate impact in kg CO2e per m³ (IVL) */
export const WATER_CO2E_PER_M3 = 0.38;

/**
 * Yearly operational water use (m³) for the whole building, summed per program.
 *
 * Each water-consuming program gets the share of the heated area (Atemp) that matches its
 * share of the building's primary area, and is multiplied by its own occupant density and
 * IVL schablon (DEV-3326). Programs that carry no occupants
 * ({@link UNOCCUPIED_ACTIVITY_TYPES}) take their share of Atemp without being charged, so a
 * building that is half garage only uses water for the other half.
 *
 * Support programs (stairwells, facades, installations, inner walls) are left out of the
 * primary area on purpose: they are shared infrastructure, so every primary program is
 * charged pro rata for them. {@link getOccupantCountPerM2} is defined per Atemp and already
 * discounts common space through `DEFAULT_LIVINGAREA_PER_HEATED_AREA_RATIO`.
 *
 * NOTE: occupant density comes from {@link getOccupantCountPerM2}, which only separates
 * office and school from residential. Retail therefore uses the residential density and
 * industry the office density until IVL densities per use type are implemented.
 * @param designVariables Calculated design expression variables.
 * @returns Water use in m³ per year, 0 when the building has no water-consuming program.
 */
export const getWaterM3PerYear = (
  designVariables: DesignExpressionVariables,
): number => {
  const { programs } = designVariables;
  const waterPrograms = WATER_PROGRAM_ACTIVITY_TYPES.map((type) => ({
    type,
    area: programs[type].gfa,
  }));
  const waterArea = waterPrograms.reduce((acc, { area }) => acc + area, 0);
  if (!waterArea) return 0;

  const unoccupiedArea = UNOCCUPIED_ACTIVITY_TYPES.reduce(
    (acc, type) => acc + programs[type].gfa,
    0,
  );
  const primaryArea = waterArea + unoccupiedArea;
  const heatedArea = getHeatedArea(designVariables.gfa);
  const laPerApartment =
    programs[ActivityType.Residential].apartments_living_area_per_apartment;

  return waterPrograms.reduce((acc, { type, area }) => {
    const occupantCount =
      getOccupantCountPerM2(type, laPerApartment) *
      heatedArea *
      (area / primaryArea);
    return acc + occupantCount * WATER_M3_PER_OCCUPANT_YEAR[type];
  }, 0);
};

/**
 * B7 operational water use for the whole building over the reference period (DEV-3326):
 * `co2e_B7 = water_m3_per_year * 0.38 kg CO2e/m³ * 50 years`.
 *
 * Building-level only — proposals can't change building area or occupants, so the
 * value is identical for the baseline and every proposal of a version.
 * @param project The project to calculate B7 for.
 * @returns Results with `co2e_B7`, or empty results when the building has no water use.
 */
export const getOperationalWaterResults = (project: Project): Results => {
  const designVariables = getDesignExpressionVariables(
    getBuildingDesignFromProjectOrBuilding(project),
  );
  if (!designVariables) return {};

  const co2e =
    getWaterM3PerYear(designVariables) *
    WATER_CO2E_PER_M3 *
    IVL_B_PHASE_REFERENCE_PERIOD_YEARS;
  return co2e ? { co2e_B7: co2e } : {};
};
